// The club pages' text face (Plan AO, as in the mock): Latin and Vietnamese only,
// three weights; the files download only when text in them is on screen.
import '@fontsource/be-vietnam-pro/latin-400.css';
import '@fontsource/be-vietnam-pro/vietnamese-400.css';
import '@fontsource/be-vietnam-pro/latin-600.css';
import '@fontsource/be-vietnam-pro/vietnamese-600.css';
import '@fontsource/be-vietnam-pro/latin-700.css';
import '@fontsource/be-vietnam-pro/vietnamese-700.css';
import { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicFooter } from './components/PublicFooter';
import { PublicHeader } from './components/PublicHeader';
import { useRevealOnScroll } from './components/motion';
import { lazyOptional } from '../lib/lazy-optional';
import { prefetchPublicPages } from '../routes/lazy-pages';
import { publicPaths } from '../routes/paths';

// The HBAAC page's own header and footer come with that page's code, not with
// every public page's first download.
const HbaacHeader = lazyOptional(() => import('./hbaac/Chrome').then((m) => ({ default: m.HbaacHeader })));
const HbaacFooter = lazyOptional(() => import('./hbaac/Chrome').then((m) => ({ default: m.HbaacFooter })));

export function PublicLayout() {
  const { pathname, hash } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useRevealOnScroll(mainRef, pathname);
  useEffect(() => prefetchPublicPages(), []);

  /**
   * Scroll behaviour for a site that mixes pages and in-page anchors.
   *
   * Navigating to a new page starts at the top; arriving with a hash scrolls
   * to that section instead. Without this, following `/#tin-tuc` from another
   * page lands mid-document at whatever offset the previous page had.
   */
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);

  // The HBAAC page is a competition's site: its own header and footer, and its
  // dark ground from top to bottom (Plan AN, as in the mock).
  const hbaac = pathname === publicPaths.competition;

  return (
    <div className={`min-h-app flex flex-col ${hbaac ? 'bg-hbaac-ink' : 'club-site bg-white font-body dark:bg-navy-900'}`}>
      {hbaac ? (
        <Suspense fallback={null}>
          <HbaacHeader />
        </Suspense>
      ) : (
        <PublicHeader />
      )}
      {/* At least a screen tall, so the footer starts below the fold and is not
          pushed down (a layout shift) when the page's data arrives. */}
      <main ref={mainRef} className="min-h-[100svh] flex-1">
        <Outlet />
      </main>
      {hbaac ? (
        <Suspense fallback={null}>
          <HbaacFooter />
        </Suspense>
      ) : (
        <PublicFooter />
      )}
    </div>
  );
}
